import { supabase } from '../lib/supabase';

/**
 * Get or create referral code for a client
 */
export const getOrCreateReferralCode = async (clientId) => {
  try {
    const { data, error } = await supabase?.rpc('get_or_create_referral_code', {
      p_client_id: clientId
    });
    if (error) throw error;
    return data;
  } catch (err) {
    console.error('[Referral] getOrCreateReferralCode error:', err);
    return null;
  }
};

/**
 * Get referral stats for a client
 */
export const getClientReferralStats = async (clientId) => {
  try {
    const { data, error } = await supabase?.rpc('get_client_referral_stats', {
      p_client_id: clientId
    });
    if (error) throw error;
    return data;
  } catch (err) {
    console.error('[Referral] getClientReferralStats error:', err);
    return null;
  }
};

/**
 * Add a referral reward record
 */
export const addReferralReward = async ({ referrerClientId, referredClientId = null, pointsAwarded, rewardType = 'referral', notes = null }) => {
  try {
    const { data, error } = await supabase?.from('referral_rewards')?.insert({
      referrer_client_id: referrerClientId,
      referred_client_id: referredClientId,
      points_awarded: pointsAwarded,
      reward_type: rewardType,
      status: 'pending',
      notes
    })?.select()?.single();
    if (error) throw error;
    return data;
  } catch (err) {
    console.error('[Referral] addReferralReward error:', err);
    return null;
  }
};

/**
 * Get loyalty level based on points
 */
export const getLoyaltyLevel = (points) => {
  const p = points || 0;
  if (p >= 200) return { label: 'Ambassadeur', color: 'from-purple-600 to-purple-800', badge: '👑', min: 200, max: null };
  if (p >= 100) return { label: 'Or', color: 'from-yellow-500 to-yellow-700', badge: '🥇', min: 100, max: 199 };
  if (p >= 50) return { label: 'Argent', color: 'from-gray-400 to-gray-600', badge: '🥈', min: 50, max: 99 };
  return { label: 'Bronze', color: 'from-orange-500 to-orange-700', badge: '🥉', min: 0, max: 49 };
};

/**
 * Get next loyalty level info
 */
export const getNextLevelInfo = (points) => {
  const p = points || 0;
  if (p >= 200) return { nextLabel: null, pointsNeeded: 0, progress: 100 };
  if (p >= 100) return { nextLabel: 'Ambassadeur', pointsNeeded: 200 - p, progress: ((p - 100) / 100) * 100 };
  if (p >= 50) return { nextLabel: 'Or', pointsNeeded: 100 - p, progress: ((p - 50) / 50) * 100 };
  return { nextLabel: 'Argent', pointsNeeded: 50 - p, progress: (p / 50) * 100 };
};

/**
 * Ensure referral code + welcome points for a client (call on dashboard load)
 * This is the main entry point that replaces the old "code non disponible" logic.
 */
export const ensureReferralAndWelcomePoints = async (clientId) => {
  try {
    const { data, error } = await supabase?.rpc('ensure_client_referral_and_welcome_points', {
      p_client_id: clientId
    });
    if (error) throw error;
    return data;
  } catch (err) {
    console.error('[Referral] ensureReferralAndWelcomePoints error:', err);
    return null;
  }
};

/**
 * Award welcome points to a client (one-time, +5 pts)
 */
export const awardWelcomePoints = async (clientId) => {
  try {
    const { data, error } = await supabase?.rpc('award_welcome_points', {
      p_client_id: clientId
    });
    if (error) throw error;
    return data;
  } catch (err) {
    console.error('[Referral] awardWelcomePoints error:', err);
    return null;
  }
};

/**
 * Generate WhatsApp share message
 */
export const generateWhatsAppShareMessage = (clientName, referralCode) => {
  const refLink = `https://adtexpress.cloud/?ref=${referralCode}`;
  const message = `🚢 *ADT Express* — Transport maritime France → Côte d'Ivoire\n\n` +
    `Bonjour ! Je suis client chez ADT Express et je vous recommande leurs services pour vos envois de barriques et colis vers Abidjan.\n\n` +
    `✅ Transport fiable et sécurisé\n` +
    `✅ Suivi en temps réel\n` +
    `✅ Offre Été 2026 : 160€ TTC tout compris\n\n` +
    `Utilisez mon code parrain *${referralCode}* pour bénéficier de 5 points de bienvenue !\n\n` +
    `🔗 ${refLink}\n\n` +
    `📞 Contact ADT Express : +33 6 51 98 86 21`;
  return encodeURIComponent(message);
};
